/**
 * Suggestion icon button registered via the UI Control Registry API, replacing the widget
 * decoration built in `calculateSuggestionsDecorations` (see `main.tsx`). Positioning is owned by
 * the block-controls right-surface host — this component only renders content, but still sets its
 * own `--button-<localId>` CSS anchor since the suggestion/follow-up cards anchor to it by name.
 */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo, useRef } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import { BLOCK_CONTROL_UI_CONTEXT } from '@atlaskit/editor-common/block-controls/block-control-ui-context';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { CommonComponentProps } from '@atlaskit/editor-ui-control-model/types';
import { token } from '@atlaskit/tokens';

import type { AiSuggestionsPlugin } from '../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../pm-plugins/main';

import { SuggestionIcon } from './SuggestionIcon';

const suggestionIconScaleVar = '--suggestion-icon-scale';
const suggestionIconRotateVar = '--suggestion-icon-rotate';
const suggestionIconOpacityVar = '--suggestion-icon-opacity';

const styles = cssMap({
	container: {
		position: 'relative',
		pointerEvents: 'all',
	},
	button: {
		display: 'block',
		borderRadius: token('radius.full'),
		[suggestionIconScaleVar]: '1',
		[suggestionIconRotateVar]: '0deg',
		[suggestionIconOpacityVar]: '1',
		'&:hover': {
			// for some reason we need a non-variable property here otherwise there's a type error
			display: 'block',
			[suggestionIconScaleVar]: 'calc(4/3)',
			[suggestionIconRotateVar]: '90deg',
			[suggestionIconOpacityVar]: '0',
		},
	},
	svgContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		transition: 'transform 240ms ease-out',
		transformOrigin: 'center center',
		transform: `scale(var(${suggestionIconScaleVar})) rotate(var(${suggestionIconRotateVar}))`,
	},
	baseIconColorLayer: {
		height: '100%',
		width: '100%',
		position: 'absolute',
		top: 0,
		left: 0,
		transition: 'opacity 240ms ease-out',
	},
	iconColor: {
		backgroundColor: token('color.icon.subtle'),
		opacity: `var(${suggestionIconOpacityVar})`,
	},
	iconColorHover: {
		opacity: `calc(1 - var(${suggestionIconOpacityVar}))`,
		background: `conic-gradient(from 45deg, ${token('color.rovo.icon.saffron')} 0deg, ${token(
			'color.rovo.icon.saffron',
		)} 90deg, ${token('color.rovo.icon.lime')} 90deg, ${token(
			'color.rovo.icon.lime',
		)} 180deg, ${token('color.rovo.icon.blue')} 180deg, ${token(
			'color.rovo.icon.blue',
		)} 270deg, ${token('color.rovo.icon.purple')} 270deg, ${token(
			'color.rovo.icon.purple',
		)} 360deg)`,
	},
});

const AnimatedIcon = () => (
	<span css={styles.svgContainer}>
		<SuggestionIcon label="" size="small">
			<div css={[styles.baseIconColorLayer, styles.iconColor]} />
			<div css={[styles.baseIconColorLayer, styles.iconColorHover]} />
		</SuggestionIcon>
	</span>
);

export type SuggestionRightSurfaceButtonProps = CommonComponentProps & {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
};

export function SuggestionRightSurfaceButton({
	api,
	surfaceContext,
}: SuggestionRightSurfaceButtonProps): JSX.Element | null {
	const { formatMessage } = useIntl();
	const context = surfaceContext?.get(BLOCK_CONTROL_UI_CONTEXT);
	const localId = context?.targetNode.node.attrs?.localId as string | undefined;

	// Derived primitives (rather than the state maps) so the hook's shallow
	// equality check can skip re-renders that don't affect this button.
	const { firstSuggestionId, isSelected, shortDescription, suggestionsCount } =
		useSharedPluginStateWithSelector(api, ['aiSuggestions'], (state) => {
			const { suggestionIdsByBaseLocalId, suggestionById, selectedSuggestion } =
				state?.aiSuggestionsState ?? {};
			const suggestionIds = localId ? suggestionIdsByBaseLocalId?.get(localId) : undefined;
			const firstSuggestionId: string | undefined = suggestionIds?.values().next().value;

			return {
				firstSuggestionId,
				isSelected: Boolean(selectedSuggestion && suggestionIds?.has(selectedSuggestion)),
				shortDescription: firstSuggestionId
					? suggestionById?.get(firstSuggestionId)?.shortDescription
					: undefined,
				suggestionsCount: suggestionIds?.size ?? 0,
			};
		});

	const isSelectingSuggestionRef = useRef(false);

	const onSelectSuggestion = useCallback(() => {
		if (isSelectingSuggestionRef.current) {
			return;
		}

		isSelectingSuggestionRef.current = true;

		const selectSuggestion = async (): Promise<void> => {
			try {
				const canCloseComment =
					(await api?.annotation?.actions.requestCloseInlineComment?.()) ?? true;

				if (!canCloseComment || !firstSuggestionId) {
					return;
				}

				api?.aiSuggestions?.actions.selectSuggestion(firstSuggestionId, {
					interactionPoint: 'card',
				});
			} finally {
				isSelectingSuggestionRef.current = false;
			}
		};

		void selectSuggestion();
	}, [api, firstSuggestionId]);

	const setHoveredSuggestion = useCallback(
		(id: string | undefined) => {
			api?.core?.actions.execute(({ tr }) => {
				tr.setMeta(aiSuggestionsPluginKey, { action: 'HOVER_SUGGESTION', id });
				return tr;
			});
		},
		[api],
	);

	const tooltip = useMemo(
		() => ({
			content:
				suggestionsCount > 1
					? formatMessage(aiSuggestionsMessages.iconButtonSuggestionsCountLabel, {
							count: suggestionsCount,
						}) ||
						(shortDescription ?? '')
					: (shortDescription ?? ''),
			position: 'top' as const,
		}),
		[formatMessage, shortDescription, suggestionsCount],
	);

	if (!localId) {
		return null;
	}

	return (
		<span css={styles.container} style={{ anchorName: `--button-${localId}` }}>
			{
				// We keep the anchorName span rendered so that the suggestion card can reference it
				!isSelected && firstSuggestionId && (
					<span
						css={styles.button}
						onMouseEnter={() => setHoveredSuggestion(firstSuggestionId)}
						onMouseLeave={() => setHoveredSuggestion(undefined)}
						onFocus={() => setHoveredSuggestion(firstSuggestionId)}
						onBlur={() => setHoveredSuggestion(undefined)}
					>
						<IconButton
							isTooltipDisabled={false}
							tooltip={tooltip}
							shape="circle"
							type="button"
							label={tooltip.content}
							onClick={onSelectSuggestion}
							appearance="subtle"
							spacing="compact"
							icon={AnimatedIcon}
						/>
					</span>
				)
			}
		</span>
	);
}
